import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import { Dropdown, Modal } from 'antd';
import { ExclamationCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useAddCargoToTrip } from 'api/trips/use-add-cargos-to-trip.ts';
import { useDeleteCargoTrip } from 'api/trips/use-delete-cargo-trip.ts';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import type { TDropdownChangeInfo } from 'types/dropdown-change-info';
import { useAddCargoToActiveTrip } from '../../../api/trips/use-add-cargo-to-active-trip';
import type { ITripCargoControlProps } from './types';

export const TripCargoItemControlComponent: React.FC<ITripCargoControlProps> = (props) => {
  const { cargoItem, cargoTripId, trip, isInTrip } = props;

  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const { mutateAsync: deleteCargoTrip } = useDeleteCargoTrip();
  const { mutateAsync: addToTrip } = useAddCargoToTrip();
  const { mutateAsync: addToActiveTrip } = useAddCargoToActiveTrip();

  const [isActiveControl, setIsActiveControl] = useState(false);

  const deactivateWithTimeout = () => {
    setTimeout(() => setIsActiveControl(false), 300);
  };

  const confirm = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await deleteCargoTrip({
          tripId: trip.id,
          cargoTripId,
          cargoId: cargoItem.cargoId,
          cargoItemId: cargoItem.id,
        });
        deactivateWithTimeout();
      },
    });
  };

  const sendAddCargoItemToTripRequest = async () => {
    if (trip.status === 'planning') {
      await addToTrip({ tripId: trip.id, cargoItemIds: [cargoItem.id], updatedCargoId: cargoItem.cargoId });
    } else {
      await addToActiveTrip({
        tripId: trip.id,
        data: { cargoItemIds: [cargoItem.id] },
        updatedCargoId: cargoItem.cargoId,
      });
    }
  };

  const handleDropdownClick: MenuProps['onClick'] = async ({ key }) => {
    if (key === 'delete') {
      await confirm();
    } else if (key === 'add') {
      await sendAddCargoItemToTripRequest();
    }
  };

  const handleDropdownOpenChange = (value: boolean, info: TDropdownChangeInfo) => {
    if (info.source === 'trigger') {
      setIsActiveControl(value);
    }
  };

  return (
    <>
      <Dropdown
        key='more'
        trigger={['click']}
        menu={{
          items: isInTrip
            ? [
                {
                  key: 'delete',
                  label: t('common:delete'),
                  icon: <DeleteOutlined />,
                },
              ]
            : [
                {
                  key: 'add',
                  label: t('common:add'),
                  icon: <PlusOutlined />,
                },
              ],
          onClick: handleDropdownClick,
        }}
        placement='bottomLeft'
        onOpenChange={handleDropdownOpenChange}>
        <MoreOutlined style={{ fontSize: 20 }} />
      </Dropdown>
      {isActiveControl ? contextHolder : null}
    </>
  );
};

export const TripCargoItemControl = withRoleGuard(TripCargoItemControlComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.transport_manager,
]);
